import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format } from "date-fns"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Convert a Date object to YYYY-MM-DD string in local timezone
 * Avoids UTC conversion issues
 */
export function dateToLocalDateString(date: Date | string | undefined | null): string {
  if (!date) return '';
  // If it's already a string in YYYY-MM-DD format, return it
  if (typeof date === 'string') {
    // If it's already in YYYY-MM-DD format, just extract that part
    return date.split('T')[0];
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Normalize various date formats to YYYY-MM-DD string
 * Handles formats like:
 * - "3/16/2025 12:00 AM"
 * - "2025-03-16"
 * - Date objects
 */
export function normalizeDateToYYYYMMDD(date: string | Date | undefined | null): string | undefined {
  if (!date) return undefined;

  try {
    // If it's already in YYYY-MM-DD format, return it
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }

    // Convert to Date object and format as YYYY-MM-DD
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Check if valid date
    if (isNaN(dateObj.getTime())) {
      return undefined;
    }

    return dateToLocalDateString(dateObj);
  } catch {
    return undefined;
  }
}


/**
 * Parse YYYY-MM-DD string as local date (not UTC)
 */
export function parseDateString(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Format a date in short format (e.g., "Jan 7, 2026")
 * For YYYY-MM-DD strings, parses as local date to avoid timezone issues
 */
export function formatDate(date: string | Date | undefined | null): string {
  if (!date) return "Not Set";
  try {
    // If it's a YYYY-MM-DD string, parse as local date
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return format(parseDateString(date), "MMM d, yyyy");
    }
    // Otherwise treat as Date or ISO string
    return format(new Date(date), "MMM d, yyyy");
  } catch {
    return String(date);
  }
}

/**
 * Format a date in long format (e.g., "January 7, 2026")
 * Note: Uses Date constructor - only suitable for ISO timestamps, not date-only strings
 */
export function formatDateLong(date: string | Date | undefined | null): string {
  if (!date) return "Not Set";
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return String(date);
  }
}

/**
 * Format seconds as a duration (e.g., 3661 -> "1h 1m 1s")
 */
export function formatDuration(seconds: number): string {
  if (seconds === 0) return "0s";

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0) parts.push(`${secs}s`);

  return parts.join(' ');
}

/**
 * Format a dollar amount (e.g., 29.99 -> "$29.99")
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

/**
 * Format cents to currency (e.g., 2999 -> "$29.99")
 */
export function formatCents(cents: number): string {
  return formatCurrency(cents / 100);
}

/**
 * Format cents to currency with null safety (e.g., 2999 -> "$29.99", null -> "—")
 */
export function formatCentsSafe(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return formatCents(cents);
}

/**
 * Convert a dollar string/number from an input to cents for the API.
 * Returns undefined if the input is empty or not a valid number.
 */
export function dollarsToCents(dollars: string | number | undefined | null): number | undefined {
  if (dollars == null || dollars === '') return undefined;
  const n = typeof dollars === 'string' ? parseFloat(dollars) : dollars;
  if (isNaN(n)) return undefined;
  return Math.round(n * 100);
}

/**
 * Convert cents to a dollar string for form input fields (e.g., 2999 -> "29.99").
 * Returns empty string if null/undefined.
 */
export function centsToDollars(cents: number | null | undefined): string {
  if (cents == null) return '';
  return (cents / 100).toFixed(2);
}

/**
 * Convert a snake_case or underscore-separated key to Title Case label.
 * e.g. "initial_offer" -> "Initial Offer"
 */
export function labelFor(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g. "data:application/pdf;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};
